import { useState, useEffect, useCallback, useRef, Fragment, useMemo } from "react";
import { useScrollHighlight } from "@/hooks/use-scroll-highlight";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar, ChevronRight, Clock, Heart, Home, Shield, Activity, AlertTriangle, Eye, ExternalLink, PawPrint } from "lucide-react";
import {
  ACTIVITY_EVENT_ICONS,
  ACTIVITY_EVENT_COLORS,
  getActivityEventLabel,
  getActivityDisplayTitle,
  formatActivityTimestamp,
  parseDateSafe,
} from "@/lib/activity-event-utils";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { ADOPTION_LEVEL_BADGE_CLASS, ADOPTION_LEVEL_EMPTY_BADGE_CLASS } from "@/lib/badge-styles";
import { StatusDaysBadge } from "@/components/animals/StatusDaysBadge";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { PageHeader } from "@/components/page-header";
import { format, subDays, formatDistanceToNow } from "date-fns";
import { InteractionDetailDrawer } from "@/components/interactions/InteractionDetailDrawer";
import { AnimalDetailDrawer } from "@/components/animals/AnimalDetailDrawer";
import { AnimalActionMenu } from "@/components/animal-action-menu";
import { EntityLinkerDialog } from "@/components/tasks/entity-linker-dialog";
import { useAuth } from "@/lib/auth-context";
import { useDashboardComponents } from "@/lib/dashboard-visibility";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import { FollowAnimalButton } from "@/components/follow-animal-button";
import { ObservationNudge } from "@/components/observation-nudge";
import { useCapacitySummary, type LocationSummaryData } from "@/hooks/use-capacity";
import { usePageTitle } from "@/hooks/use-page-title";
import { LocationStrip } from "@/components/dashboard/location-strip";
import { AdminCallouts } from "@/components/dashboard/admin-callouts";
import { DashboardTasksWidget } from "@/components/dashboard/dashboard-tasks-widget";
import { DashboardMeetingsWidget } from "@/components/dashboard/dashboard-meetings-widget";
import { MetricWidget } from "@/components/metrics/MetricWidget";

// Interface for dashboard behavior alerts interactions
interface DashboardInteraction {
  id: string;
  animalId: string;
  animalName: string | null;
  animalSpecies: string | null;
  animalPhotoUrl: string | null;
  kennelStatusNorm: string | null;
  evaluationStatus: string | null;
  locationCode: string;
  zoneCode: string;
  eventType: string;
  scoreCode: string | null;
  scoreBand: string | null;
  occurredAt: string;
  author: string | null;
  rawNote: string | null;
  sourceReportId: string | null;
  sourceEmailId: string | null;
  isFlagged: boolean;
  flagReasons: Array<{ type: "long_note" | "keyword" | "highlighted"; charCount?: number; matches?: string[]; userId?: string; createdAt?: string }> | null;
}

interface DashboardMetrics {
  totalAnimals: number;
  byLocation: Array<{
    location: string; // Location code for logic/test IDs
    locationLabel: string; // Gate F18.2: Display label for UI
    total: number;
    dogs: number;
    cats: number;
    trend: number;
    lastReportDate: string | null; // Gate F18.2: Report coverage date (YYYY-MM-DD)
    lastUploadedAt: string | null; // Gate F18.2: Upload timestamp (ISO UTC)
  }>;
  capacityOverages: Array<{
    zone: string;
    location: string;
    expected: number;
    actual: number;
  }>;
}

// Gate I7: Recently viewed animal interface
interface RecentAnimal {
  id: string;
  animalId: string;
  name: string;
  species: string;
  primaryBreed: string | null;
  location: string | null;
  locationLabel: string | null;
  zoneCode: string | null;
  kennelStatusNorm: string | null;
  substatusNorm: string | null;
  kennelStatusSince?: string | null;
  kennelStatusSinceIsApprox?: boolean;
  adoptionLevelNorm: string | null;
  photoUrl: string | null;
  lastViewedAt: string;
}

interface FollowedAnimalActivity {
  animalId: string;
  eventType: string;
  title: string;
  message: string | null;
  metadata: string | Record<string, unknown> | null;
  refId: string | null;
  createdAt: string;
  occurredAt: string | null;
}

interface FollowedAnimal {
  animalId: string;
  followedAt: string;
  name: string | null;
  species: string | null;
  primaryBreed: string | null;
  photoUrl: string | null;
  location: string | null;
  zoneCode: string | null;
  kennelStatusNorm: string | null;
  substatusNorm: string | null;
  kennelStatusSince?: string | null;
  kennelStatusSinceIsApprox?: boolean;
  adoptionLevelNorm: string | null;
  outcomeDate: string | null;
  outcomeReason: string | null;
  locationLabel: string | null;
  lastObservationDate: string | null;
  totalActivityCount: number;
  recentActivity: FollowedAnimalActivity[];
}


function getFollowPreviewLink(evt: FollowedAnimalActivity): string | null {
  switch (evt.eventType) {
    case "behavior_alert":
    case "alert_flag":
      return `/animals/${evt.animalId}?tab=behavior`;
    case "file_upload": {
      const fileMeta = typeof evt.metadata === "string" ? (() => { try { return JSON.parse(evt.metadata as string); } catch { return null; } })() : evt.metadata;
      const fId = fileMeta?.fileId;
      return `/animals/${evt.animalId}?tab=files${fId ? `&fileId=${fId}` : ""}`;
    }
    case "location_transfer":
      return `/animals/${evt.animalId}?tab=stays`;
    case "outcome":
      return `/animals/${evt.animalId}`;
    case "task_linked":
      return evt.refId ? `/tasks/${evt.refId}` : null;
    case "meeting_linked":
      return evt.refId ? `/meetings/${evt.refId}` : null;
    case "observation_added_to_task":
    case "observation_added_to_meeting":
      return null;
    case "status_change":
    case "adoption_level_change":
      return `/animals/${evt.animalId}`;
    case "observation_logged":
      return `/animals/${evt.animalId}?tab=behavior`;
    default:
      return null;
  }
}

function getObservationInteractionId(evt: FollowedAnimalActivity): string | null {
  if (evt.eventType !== "observation_added_to_task" && evt.eventType !== "observation_added_to_meeting" && evt.eventType !== "observation_logged") return null;
  const meta = typeof evt.metadata === "string" ? (() => { try { return JSON.parse(evt.metadata as string); } catch { return null; } })() : evt.metadata;
  return meta?.interactionId ?? null;
}

type RecentViewedTab = "recent" | "following";

export default function Dashboard() {
  usePageTitle("Dashboard");

  // Gate I10: State for interaction detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState<DashboardInteraction | null>(null);

  const [animalDrawerOpen, setAnimalDrawerOpen] = useState(false);
  const [animalDrawerId, setAnimalDrawerId] = useState<string | null>(null);
  const [linkerOpen, setLinkerOpen] = useState(false);
  const [linkerAnimalId, setLinkerAnimalId] = useState<string | null>(null);
  const [linkerAnimalName, setLinkerAnimalName] = useState<string | undefined>(undefined);
  const [linkerAnimalSpecies, setLinkerAnimalSpecies] = useState<string | undefined>(undefined);
  const [linkerAnimalPhotoUrl, setLinkerAnimalPhotoUrl] = useState<string | null | undefined>(undefined);
  
  const [activeTab, setActiveTab] = useState<RecentViewedTab>("following");
  const [expandedFollowId, setExpandedFollowId] = useState<string | null>(null);

  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const locationCodes = user?.locationCodes ?? [];
  const { visible: dashboardVisible } = useDashboardComponents();

  const [showAllLocations, setShowAllLocations] = useState(false);

  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];

  const { highlightedId: highlightedCalloutId, trigger: triggerCalloutHighlight } = useScrollHighlight({ timeout: 2500 });
  const { highlightedId: highlightedFollowId, trigger: triggerFollowHighlight } = useScrollHighlight({ timeout: 2500 });
  const highlightPendingRef = useRef(false);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [reminderNav, setReminderNav] = useState<{ reminderId: string | null; token: number } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    const highlight = params.get("highlight");
    if (tab === "reminders") {
      const rid = highlight && highlight.startsWith("reminder-") ? highlight.replace("reminder-", "") : null;
      setReminderNav({ reminderId: rid, token: Date.now() });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const handler = (e: CustomEvent<{ reminderId: string }>) => {
      setReminderNav({ reminderId: e.detail.reminderId, token: Date.now() });
      window.history.replaceState({}, "", window.location.pathname);
    };
    window.addEventListener("reminder-tab-navigate", handler as EventListener);
    return () => window.removeEventListener("reminder-tab-navigate", handler as EventListener);
  }, []);

  const [adminBannerExpanded, setAdminBannerExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem("admin-banner-collapsed");
      return stored === "false";
    } catch { return false; }
  });

  useEffect(() => {
    const handler = (e: CustomEvent<{ expanded: boolean }>) => {
      setAdminBannerExpanded(e.detail.expanded);
    };
    window.addEventListener("admin-banner-visibility", handler as EventListener);
    return () => window.removeEventListener("admin-banner-visibility", handler as EventListener);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("highlight") === "admin-callouts" && isAdmin) {
      try { localStorage.setItem("admin-banner-collapsed", "false"); } catch {}
      setAdminBannerExpanded(true);
      highlightPendingRef.current = true;
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (highlightPendingRef.current && adminBannerExpanded) {
      highlightPendingRef.current = false;
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => {
        highlightTimerRef.current = null;
        triggerCalloutHighlight("admin-callouts-bar", "admin-callouts-bar");
      }, 300);
    }
  }, [adminBannerExpanded, triggerCalloutHighlight]);

  useEffect(() => {
    const handler = () => {
      if (adminBannerExpanded) {
        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => {
          highlightTimerRef.current = null;
          triggerCalloutHighlight("admin-callouts-bar", "admin-callouts-bar");
        }, 100);
      } else {
        try { localStorage.setItem("admin-banner-collapsed", "false"); } catch {}
        setAdminBannerExpanded(true);
        highlightPendingRef.current = true;
      }
    };
    window.addEventListener("highlight-admin-callouts", handler);
    return () => window.removeEventListener("highlight-admin-callouts", handler);
  }, [adminBannerExpanded, triggerCalloutHighlight]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const handleCollapseBanner = useCallback(() => {
    setAdminBannerExpanded(false);
    try { localStorage.setItem("admin-banner-collapsed", "true"); } catch {}
  }, []);
  
  const handleOpenInteractionDrawer = (interaction: DashboardInteraction) => {
    setSelectedInteraction(interaction);
    setDrawerOpen(true);
  };

  const handleOpenObservationDrawer = async (interactionId: string) => {
    try {
      const res = await fetch(`/api/interactions/${interactionId}`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setSelectedInteraction(data as DashboardInteraction);
      setDrawerOpen(true);
    } catch { /* ignore */ }
  };

  const { data: metrics, isLoading, isError, error, refetch } = useQuery<DashboardMetrics>({
    queryKey: ["/api/dashboard/metrics"],
  });

  const { data: capacitySummary } = useCapacitySummary();
  const capacityByLocation = capacitySummary?.locationSummaries?.reduce<Record<string, LocationSummaryData>>(
    (acc, loc) => { acc[loc.location] = loc; return acc; },
    {}
  ) ?? {};

  // Gate I7: Fetch recently viewed animals for current user
  const { data: recentAnimals, isLoading: recentAnimalsLoading } = useQuery<RecentAnimal[]>({
    queryKey: ["/api/users/me/recent-animals"],
    staleTime: 0,
  });

  const { data: followedAnimalsData, isLoading: followedAnimalsLoading } = useQuery<{ animals: FollowedAnimal[] }>({
    queryKey: ["/api/users/me/followed-animals"],
    enabled: activeTab === "following",
  });
  const followedAnimals = followedAnimalsData?.animals ?? [];

  const [pendingFollowAnimal, setPendingFollowAnimal] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    const animalParam = params.get("animal");

    if (tabParam === "following") {
      setActiveTab("following");
      if (animalParam) {
        setExpandedFollowId(animalParam);
        setPendingFollowAnimal(animalParam);
      }
      params.delete("tab");
      params.delete("animal");
      const clean = params.toString();
      window.history.replaceState({}, "", clean ? `/?${clean}` : "/");
    }
  }, []);

  useEffect(() => {
    if (!pendingFollowAnimal || activeTab !== "following" || followedAnimals.length === 0) return;
    const target = followedAnimals.find(a => a.animalId === pendingFollowAnimal);
    if (!target) {
      setPendingFollowAnimal(null);
      return;
    }
    const attempt = (retries: number) => {
      const el = document.getElementById(`follow-row-${pendingFollowAnimal}`);
      if (el) {
        triggerFollowHighlight(pendingFollowAnimal, `follow-row-${pendingFollowAnimal}`);
        setPendingFollowAnimal(null);
      } else if (retries > 0) {
        requestAnimationFrame(() => attempt(retries - 1));
      } else {
        setPendingFollowAnimal(null);
      }
    };
    requestAnimationFrame(() => attempt(10));
  }, [pendingFollowAnimal, activeTab, followedAnimals, triggerFollowHighlight]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ animal: string }>).detail;
      setActiveTab("following");
      setExpandedFollowId(detail.animal);
      setPendingFollowAnimal(detail.animal);
      window.history.replaceState({}, "", "/");
    };
    window.addEventListener("follow-tab-navigate", handler);
    return () => window.removeEventListener("follow-tab-navigate", handler);
  }, []);

  const [alertsPeriod, setAlertsPeriod] = useState<string>("today");
  const [alertsLocation, setAlertsLocation] = useState<string>(() => {
    try {
      const saved = localStorage.getItem("mspca-dashboard-default-location");
      return saved ?? "all";
    } catch {
      return "all";
    }
  });

  const today = new Date();
  const yesterday = subDays(today, 1);
  const alertsStartDate = alertsPeriod === "today"
    ? format(today, "yyyy-MM-dd")
    : alertsPeriod === "yesterday"
    ? format(yesterday, "yyyy-MM-dd")
    : format(subDays(today, parseInt(alertsPeriod)), "yyyy-MM-dd");
  const alertsEndDate = alertsPeriod === "yesterday"
    ? format(yesterday, "yyyy-MM-dd")
    : format(today, "yyyy-MM-dd");
  const viewAllAlertsUrl = `/interactions?startDate=${alertsStartDate}&endDate=${alertsEndDate}${alertsLocation !== "all" ? `&locationCode=${alertsLocation}` : ""}`;
  
  // Metric Builder: Fetch published dashboard metrics
  const { data: publishedMetricsData } = useQuery<{ metrics: Array<{ id: string; label: string; description: string | null; targetPages: string[]; displayFields?: string[]; defaultSortField?: string | null; defaultSortDirection?: string | null; chartEnabled?: boolean; defaultView?: string; resultType?: string; conditions?: { conditions: Array<{ field: string }> } }> }>({
    queryKey: ["/api/metrics", "published", "dashboard"],
    queryFn: async () => {
      const res = await fetch("/api/metrics?status=published&target=dashboard", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch metrics");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
  const dashboardMetricsList = publishedMetricsData?.metrics ?? [];

  // Gate I10: Fetch individual high-severity interactions for the dashboard
  const { data: dashboardInteractionsData, isLoading: behaviorAlertsLoading } = useQuery<{
    interactions: DashboardInteraction[];
    pagination: { total: number };
  }>({
    queryKey: ["/api/interactions", "dashboard-alerts", alertsStartDate, alertsEndDate, alertsLocation],
    queryFn: async () => {
      const params = new URLSearchParams({
        startDate: alertsStartDate,
        endDate: alertsEndDate,
        scoreBand: "RED",
        sortBy: "occurred_at",
        sortDir: "desc",
        limit: "100",
        dedup: "true",
      });
      if (alertsLocation !== "all") params.set("locationCode", alertsLocation);
      const response = await fetch(`/api/interactions?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch behavior interactions");
      return response.json();
    },
  });
  
  // High severity interactions from the last 7 days
  const highSeverityInteractions = dashboardInteractionsData?.interactions || [];

  const sortedLocations = useMemo(() => {
    const byLocation = metrics?.byLocation ?? [];
    if (locationCodes.length === 0) return byLocation;
    const mySet = new Set(locationCodes);
    const mine = locationCodes
      .map(code => byLocation.find(l => l.location === code))
      .filter((l): l is NonNullable<typeof l> => l != null);
    const rest = byLocation.filter(l => !mySet.has(l.location));
    return [...mine, ...rest];
  }, [metrics?.byLocation, locationCodes]);

  const visibleLocations = useMemo(() => {
    if (showAllLocations || locationCodes.length === 0) return sortedLocations;
    return sortedLocations.filter(l => locationCodes.includes(l.location));
  }, [sortedLocations, showAllLocations, locationCodes]);

  const canToggleLocationScope =
    locationCodes.length > 0 && sortedLocations.some(l => !locationCodes.includes(l.location));

  if (isLoading) {
    return (
      <div className="flex-1 space-y-6 px-4 sm:px-6 pb-6 pt-4 overflow-x-hidden">
        <PageHeader
          breadcrumb={[{ label: "Dashboard" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-dashboard">
              <Home className="w-6 h-6 text-primary" />
              Dashboard
            </span>
          }
        />
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 rounded-md" />
          ))}
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle error state with retry
  if (isError) {
    return (
      <div className="flex-1 space-y-6 px-4 sm:px-6 pb-6 pt-4 overflow-x-hidden">
        <PageHeader
          breadcrumb={[{ label: "Dashboard" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-dashboard">
              <Home className="w-6 h-6 text-primary" />
              Dashboard
            </span>
          }
        />
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Dashboard</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : "Failed to load dashboard data"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-8 text-center">
              <Button onClick={() => refetch()} variant="outline" data-testid="button-retry-dashboard">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Shouldn't happen but handle undefined metrics
  if (!metrics) {
    return null;
  }

  const data = metrics;

  return (
    <div className="flex-1 space-y-6 px-4 sm:px-6 pb-6 pt-4 overflow-x-hidden">
      <PageHeader
        breadcrumb={[{ label: "Dashboard" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-dashboard">
            <Home className="w-6 h-6 text-primary" />
            Dashboard
          </span>
        }
        actions={null}
      />

      {dashboardVisible.has("admin_callouts") && isAdmin && adminBannerExpanded && (
        <AdminCallouts
          totalAnimals={data.totalAnimals}
          avgCapacityPercent={capacitySummary?.systemSummary?.utilizationConfigured != null
            ? Math.round(capacitySummary.systemSummary.utilizationConfigured)
            : null}
          capacityStatus={capacitySummary?.systemSummary?.status === "over" ? "over"
            : capacitySummary?.systemSummary?.status === "near" ? "near"
            : capacitySummary?.systemSummary?.status === "ok" ? "ok"
            : "unknown"}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          onCollapse={handleCollapseBanner}
          highlightClassName={highlightedCalloutId === "admin-callouts-bar" ? "ring-2 ring-primary ring-inset bg-primary/5 animate-pulse rounded-md" : undefined}
        />
      )}

      {/* Location Strip with Collapsible Detail Panel */}
      {dashboardVisible.has("location_strip") && (data.totalAnimals === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No Animals</CardTitle>
            <CardDescription>Upload daily intake reports to see population data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-8 text-center text-muted-foreground">
              <p className="mb-2">No animal data available yet.</p>
              <p className="mb-4 text-sm">Upload today's intake reports on the Reports page to begin tracking animals across all locations.</p>
              <Button variant="outline" asChild>
                <Link href="/reports" data-testid="button-upload-reports">
                  Upload Reports
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {locationCodes.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="text-no-locations-nudge">
              Showing all locations.{" "}
              <Link href="/account" className="underline underline-offset-2 hover:text-foreground transition-colors">
                Set your locations
              </Link>{" "}
              to personalize this view.
            </p>
          )}
          <LocationStrip
            locations={visibleLocations}
            capacityByLocation={capacityByLocation}
            initialLocation={user?.defaultLocationCode ?? locationCodes[0]}
            sessionLocationCodes={locationCodes}
            canToggleLocationScope={canToggleLocationScope}
            showAllLocations={showAllLocations}
            onToggleAllLocations={() => setShowAllLocations(v => !v)}
            sortedLocationsCount={sortedLocations.length}
            onViewAnimalPreview={(id) => {
              setAnimalDrawerId(id);
              setAnimalDrawerOpen(true);
            }}
            onAddAnimalTo={(id, name, species, photoUrl) => {
              setLinkerAnimalId(id);
              setLinkerAnimalName(name);
              setLinkerAnimalSpecies(species);
              setLinkerAnimalPhotoUrl(photoUrl ?? undefined);
              setLinkerOpen(true);
            }}
            onLocationExpanded={(loc) => setAlertsLocation(loc ?? "all")}
          />
        </div>
      ))}

      {/* Row 1: Recently Viewed (50%) + Behavior Alerts (50%) */}
      <div className="grid gap-4 lg:grid-cols-2">

        {/* Following / Recently Viewed — tabbed card */}
        <Card className="min-w-0 overflow-hidden min-h-[420px] lg:min-h-[320px] flex flex-col" data-testid="card-recently-viewed" data-tour="dashboard-recently-viewed">
          <CardHeader className="pb-0 border-b flex flex-row items-center gap-2 space-y-0 px-4 pt-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-wider font-semibold text-muted-foreground/60 mr-2">
              Animals
            </span>
            <div className="flex items-center gap-1 -mb-px">
              <button
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "following"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setActiveTab("following")}
                data-testid="tab-following"
              >
                <Heart className="h-4 w-4" />
                Following
              </button>
              <button
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "recent"
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setActiveTab("recent")}
                data-testid="tab-recent"
              >
                <Clock className="h-4 w-4" />
                Recently Viewed
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 flex flex-col">
            {activeTab === "recent" ? (
              <>
                {recentAnimalsLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1.5 flex-1">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !recentAnimals || recentAnimals.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground" data-testid="text-no-recent-animals">
                    <div>
                      <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm">No recently viewed animals yet</p>
                      <p className="text-xs mt-1">Visit animal detail pages to build your history</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm table-zebra">
                    <thead>
                      <tr className="border-b text-[10px] text-muted-foreground uppercase tracking-wider">
                        <th className="text-left py-2 px-4 font-medium">Animal</th>
                        <th className="text-center py-2 px-4 font-medium w-[60px]">Level</th>
                        <th className="text-center py-2 px-4 font-medium w-[90px]">Status</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentAnimals.slice(0, 6).map((animal) => (
                        <tr
                          key={animal.id}
                          className="border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50"
                          data-testid={`row-recent-animal-${animal.animalId}`}
                        >
                          <td className="py-2 px-4">
                            <Link
                              href={`/animals/${animal.animalId}`}
                              className="flex items-center gap-3 min-w-0"
                              data-testid={`link-recent-animal-${animal.animalId}`}
                            >
                              <AnimalAvatar
                                animalId={animal.animalId}
                                photoUrl={animal.photoUrl ?? null}
                                name={animal.name}
                                species={animal.species}
                                className="h-8 w-8 flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1 min-w-0">
                                  <p className="text-sm font-semibold truncate">{animal.name}</p>
                                  {followedIds.includes(animal.animalId) && (
                                    <Heart className="h-3 w-3 flex-shrink-0 fill-rose-500 text-rose-500" data-testid={`icon-followed-recent-${animal.animalId}`} />
                                  )}
                                </div>
                                <p className="text-[10px] text-muted-foreground/60">
                                  {animal.animalId}
                                  {animal.locationLabel ? ` \u00B7 ${animal.locationLabel}` : ""}
                                </p>
                              </div>
                            </Link>
                          </td>
                          <td className="py-2 px-4 text-center">
                            <Badge
                              variant="outline"
                              className={`text-[9px] h-4 px-1.5 font-mono ${animal.adoptionLevelNorm ? ADOPTION_LEVEL_BADGE_CLASS : ADOPTION_LEVEL_EMPTY_BADGE_CLASS}`}
                              data-testid={`badge-recent-adoption-${animal.animalId}`}
                            >
                              {animal.adoptionLevelNorm || "\u2013"}
                            </Badge>
                          </td>
                          <td className="py-2 px-4 text-center">
                            <StatusDaysBadge
                              status={animal.kennelStatusNorm}
                              substatus={animal.substatusNorm}
                              statusSince={animal.kennelStatusSince}
                              isApprox={animal.kennelStatusSinceIsApprox}
                              data-testid={`badge-recent-kennel-${animal.animalId}`}
                              className="text-[9px] h-4 px-1.5"
                            />
                          </td>
                          <td className="py-2 pr-2" onClick={(e) => e.stopPropagation()}>
                            <AnimalActionMenu
                              animalId={animal.animalId}
                              animalName={animal.name}
                              onViewPreview={(id) => {
                                setAnimalDrawerId(id);
                                setAnimalDrawerOpen(true);
                              }}
                              onAddTo={(id, name, species, photoUrl) => {
                                setLinkerAnimalId(id);
                                setLinkerAnimalName(name);
                                setLinkerAnimalSpecies(species);
                                setLinkerAnimalPhotoUrl(photoUrl);
                                setLinkerOpen(true);
                              }}
                              animalSpecies={animal.species}
                              animalPhotoUrl={animal.photoUrl}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            ) : (
              <>
                {followedAnimalsLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1.5 flex-1">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : followedAnimals.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground" data-testid="text-no-followed-animals">
                    <div>
                      <Heart className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm">No followed animals yet</p>
                      <p className="text-xs mt-1">Follow animals from their profile to track activity here</p>
                      <Link href="/animals" className="text-xs text-primary mt-2 inline-block" data-testid="link-view-animals">View current animals</Link>
                    </div>
                  </div>
                ) : (
                  <div>
                  <table className="w-full text-sm table-zebra">
                    <thead>
                      <tr className="border-b text-[10px] text-muted-foreground uppercase tracking-wider">
                        <th className="text-left py-2 px-3 font-medium">Animal</th>
                        <th className="text-center py-2 px-1 font-medium w-[52px] hidden sm:table-cell">Level</th>
                        <th className="text-center py-2 px-1 font-medium w-[80px] hidden sm:table-cell">Status</th>
                        <th className="py-2 pr-1 w-0"></th>
                      </tr>
                    </thead>
                    <tbody>
                    {followedAnimals.map((animal) => {
                      const isExpanded = expandedFollowId === animal.animalId;
                      const isHighlighted = highlightedFollowId === animal.animalId;
                      return (
                        <Fragment key={animal.animalId}>
                        <tr
                          id={`follow-row-${animal.animalId}`}
                          className={`border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50 ${isHighlighted ? "ring-2 ring-primary ring-inset bg-primary/5" : ""}`}
                          data-testid={`row-followed-animal-${animal.animalId}`}
                        >
                          <td className="py-2 px-3">
                            <Link
                              href={`/animals/${animal.animalId}`}
                              className="flex items-center gap-3 min-w-0"
                              data-testid={`link-followed-animal-${animal.animalId}`}
                            >
                              <AnimalAvatar
                                animalId={animal.animalId}
                                photoUrl={animal.photoUrl}
                                name={animal.name || animal.animalId}
                                species={animal.species || "DOG"}
                                className="h-8 w-8 flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold truncate">{animal.name || animal.animalId}</p>
                                </div>
                                <p className="text-[10px] text-muted-foreground/60">
                                  {animal.animalId}
                                  {animal.locationLabel ? ` \u00B7 ${animal.locationLabel}` : ""}
                                </p>
                              </div>
                            </Link>
                          </td>
                          <td className="py-2 px-1 text-center hidden sm:table-cell">
                            <Badge
                              variant="outline"
                              className={`text-[9px] h-4 px-1.5 font-mono ${animal.adoptionLevelNorm ? ADOPTION_LEVEL_BADGE_CLASS : ADOPTION_LEVEL_EMPTY_BADGE_CLASS}`}
                              data-testid={`badge-follow-adoption-${animal.animalId}`}
                            >
                              {animal.adoptionLevelNorm || "\u2013"}
                            </Badge>
                          </td>
                          <td className="py-2 px-1 text-center hidden sm:table-cell">
                            <StatusDaysBadge
                              status={animal.kennelStatusNorm}
                              substatus={animal.substatusNorm}
                              statusSince={animal.kennelStatusSince}
                              isApprox={animal.kennelStatusSinceIsApprox}
                              data-testid={`badge-follow-kennel-${animal.animalId}`}
                              className="text-[9px] h-4 px-1.5"
                            />
                          </td>
                          <td className="py-2 pr-1" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-0.5 justify-end">
                              <ObservationNudge lastObservationDate={animal.lastObservationDate} testIdSuffix={animal.animalId} />
                              <FollowAnimalButton animalId={animal.animalId} animalName={animal.name || undefined} />
                              {animal.recentActivity.length > 0 && (
                                <ExpandCollapseButton
                                  expanded={isExpanded}
                                  onClick={() => setExpandedFollowId(isExpanded ? null : animal.animalId)}
                                  expandLabel="Activity"
                                  collapseLabel="Activity"
                                  testId={`button-expand-follow-${animal.animalId}`}
                                />
                              )}
                              <AnimalActionMenu
                                animalId={animal.animalId}
                                animalName={animal.name || undefined}
                                onViewPreview={(id) => {
                                  setAnimalDrawerId(id);
                                  setAnimalDrawerOpen(true);
                                }}
                                onAddTo={(id, name, species, photoUrl) => {
                                  setLinkerAnimalId(id);
                                  setLinkerAnimalName(name);
                                  setLinkerAnimalSpecies(species);
                                  setLinkerAnimalPhotoUrl(photoUrl);
                                  setLinkerOpen(true);
                                }}
                                animalSpecies={animal.species || undefined}
                                animalPhotoUrl={animal.photoUrl}
                              />
                            </div>
                          </td>
                        </tr>
                        {isExpanded && animal.recentActivity.length > 0 && (
                          <tr data-testid={`activity-panel-row-${animal.animalId}`}>
                            <td colSpan={4} className="px-4 pb-3 pt-3" data-testid={`activity-panel-${animal.animalId}`}>
                              <div className="ml-11 border-l-2 border-muted pl-3 space-y-2.5">
                                {animal.recentActivity.map((evt, idx) => {
                                  const EvtIcon = ACTIVITY_EVENT_ICONS[evt.eventType] ?? Activity;
                                  const dotColor = ACTIVITY_EVENT_COLORS[evt.eventType] ?? "bg-muted-foreground";
                                  const label = getActivityEventLabel(evt.eventType);
                                  const { displayTitle, subtitle } = getActivityDisplayTitle(
                                    evt.eventType, evt.title, "event", animal.name,
                                  );
                                  const evtLink = getFollowPreviewLink(evt);
                                  const observationId = getObservationInteractionId(evt);
                                  return (
                                    <div key={idx} className="flex items-start gap-2.5">
                                      <span className={`mt-1 flex-shrink-0 h-4 w-4 rounded-full ${dotColor} flex items-center justify-center`}>
                                        <EvtIcon className="h-2.5 w-2.5 text-white" />
                                      </span>
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <Badge variant="outline" className="text-[9px] h-4 px-1.5 flex-shrink-0">
                                            {label}
                                          </Badge>
                                          <span className="text-[10px] text-muted-foreground/50" title={format(parseDateSafe(evt.occurredAt ?? evt.createdAt), "PPpp")}>
                                            {formatActivityTimestamp(parseDateSafe(evt.occurredAt ?? evt.createdAt))}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1 mt-0.5">
                                          <p className="text-xs leading-tight min-w-0 truncate">{displayTitle}</p>
                                          {observationId ? (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleOpenObservationDrawer(observationId); }}
                                              data-testid={`button-observation-preview-${idx}`}
                                            >
                                              <Eye className="h-3 w-3 flex-shrink-0 text-muted-foreground/50 hover:text-primary" />
                                            </button>
                                          ) : evtLink ? (
                                            <Link href={evtLink} onClick={(e: React.MouseEvent) => e.stopPropagation()} data-testid={`link-activity-preview-${idx}`}>
                                              <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground/50 hover:text-primary" />
                                            </Link>
                                          ) : null}
                                        </div>
                                        {subtitle && (
                                          <p className="text-[10px] text-muted-foreground/60 line-clamp-1 mt-0.5">{subtitle}</p>
                                        )}
                                        {evt.message && (
                                          <p className="text-[10px] text-muted-foreground/60 line-clamp-1 mt-0.5">{evt.message}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="ml-11 pl-3 mt-2">
                                <Link
                                  href={`/animals/${animal.animalId}`}
                                  className="text-xs text-primary"
                                  data-testid={`link-view-all-activity-${animal.animalId}`}
                                >
                                  View all activity
                                </Link>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                      );
                    })}
                    </tbody>
                  </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* High Severity Alerts */}
        {dashboardVisible.has("behavior_alerts") && <Card className="border-alert/30 overflow-hidden min-w-0 min-h-[320px] flex flex-col" data-testid="card-dashboard-behavior-alerts" data-tour="dashboard-high-severity-alerts">
          <CardHeader className="bg-alert/5 border-b border-alert/10 flex flex-row items-center justify-between gap-2 space-y-0 px-4 pt-3 pb-2.5">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-alert" />
              <CardTitle className="text-base">Behavior Alerts</CardTitle>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={alertsPeriod} onValueChange={setAlertsPeriod}>
                <SelectTrigger className="h-7 w-auto text-xs gap-1.5 pr-2" data-testid="select-alerts-period">
                  <Calendar className="h-3 w-3 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                </SelectContent>
              </Select>
              <Select value={alertsLocation} onValueChange={setAlertsLocation}>
                <SelectTrigger className="h-7 w-auto text-xs gap-1.5 pr-2" data-testid="select-alerts-location">
                  <Home className="h-3 w-3 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {(metrics?.byLocation ?? []).map((loc) => (
                    <SelectItem key={loc.location} value={loc.location}>
                      {loc.locationLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {highSeverityInteractions.length > 0 && (
                <Badge className="bg-alert text-alert-foreground">{highSeverityInteractions.length}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 flex flex-col">
            {behaviorAlertsLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <div className="space-y-1.5 flex-1">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                ))}
              </div>
            ) : highSeverityInteractions.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-muted-foreground" data-testid="text-no-behavior-alerts">
                <div>
                  <Shield className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-sm">
                    {(() => {
                      const locSuffix = alertsLocation !== "all" ? ` at ${(metrics?.byLocation ?? []).find(l => l.location === alertsLocation)?.locationLabel ?? alertsLocation}` : "";
                      return alertsPeriod === "today"
                        ? `No behavior alerts recorded today${locSuffix}.`
                        : alertsPeriod === "yesterday"
                        ? `No behavior alerts recorded yesterday${locSuffix}.`
                        : `No behavior alerts recorded in the last ${alertsPeriod} days${locSuffix}.`;
                    })()}
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-[480px]">
              <div className="divide-y list-zebra">
                {highSeverityInteractions.map((interaction) => {
                  const zoneUpper = (interaction.zoneCode || "").toUpperCase();
                  const isCat = zoneUpper.includes("CAT");
                  const species = interaction.animalSpecies || (isCat ? "CAT" : "DOG");
                  const isScore5 = interaction.scoreCode === "5";

                  return (
                    <div
                      key={interaction.id}
                      className="px-4 py-3 flex items-start gap-3 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => handleOpenInteractionDrawer(interaction)}
                      data-testid={`button-behavior-alert-${interaction.id}`}
                    >
                      <AnimalAvatar
                        animalId={interaction.animalId}
                        photoUrl={interaction.animalPhotoUrl ?? null}
                        name={interaction.animalName || interaction.animalId}
                        species={species}
                        className="h-9 w-9 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-sm font-bold truncate">
                              {interaction.animalName || interaction.animalId}
                            </span>
                            {followedIds.includes(interaction.animalId) && (
                              <Heart className="h-3 w-3 flex-shrink-0 fill-rose-500 text-rose-500" data-testid={`icon-followed-alert-${interaction.animalId}`} />
                            )}
                            <span className="text-[10px] text-muted-foreground/60 flex-shrink-0">({species})</span>
                            {interaction.scoreCode && (
                              <Badge
                                variant="outline"
                                className={`text-[9px] h-4 px-1.5 uppercase font-bold border-transparent flex-shrink-0 ${
                                  isScore5
                                    ? "bg-destructive/10 text-destructive"
                                    : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                }`}
                                data-testid={`badge-score-${interaction.id}`}
                              >
                                {interaction.scoreCode}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[9px] h-4 px-1.5 uppercase bg-muted/60 text-muted-foreground/60 border-transparent flex-shrink-0">
                              {interaction.locationCode}
                            </Badge>
                          </div>
                          <span className="text-[10px] text-muted-foreground/50 whitespace-nowrap flex-shrink-0">
                            {(() => { const d = new Date(interaction.occurredAt.endsWith('Z') ? interaction.occurredAt : interaction.occurredAt + 'Z'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; const h = d.getUTCHours(); const m = d.getUTCMinutes().toString().padStart(2,'0'); const ampm = h >= 12 ? 'PM' : 'AM'; return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${h % 12 || 12}:${m} ${ampm}`; })()}
                          </span>
                        </div>
                        {interaction.rawNote && (
                          <p className="text-[11px] text-muted-foreground/70 mt-0.5 line-clamp-2">{interaction.rawNote}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>
            )}
          </CardContent>
          {highSeverityInteractions.length > 0 && (
            <div className="p-3 border-t bg-muted/30">
              <Button variant="ghost" className="w-full text-xs text-muted-foreground" asChild>
                <Link href={viewAllAlertsUrl} data-testid="button-view-all-behavior-alerts">
                  View All Interactions <ChevronRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </div>
          )}
        </Card>}
      </div>

      {/* Row 2: Tasks (50%) + Meetings (50%) */}
      {(dashboardVisible.has("my_tasks") || dashboardVisible.has("reminders") || dashboardVisible.has("meetings")) && (
      <div className="grid gap-4 lg:grid-cols-2">
        {(dashboardVisible.has("my_tasks") || dashboardVisible.has("reminders")) && (
          <div className="min-w-0 min-h-[320px]">
            <DashboardTasksWidget
              reminderNav={reminderNav}
              showTasks={dashboardVisible.has("my_tasks")}
              showReminders={dashboardVisible.has("reminders")}
            />
          </div>
        )}
        {dashboardVisible.has("meetings") && (
          <div className="min-w-0 min-h-[320px]">
            <DashboardMeetingsWidget />
          </div>
        )}
      </div>
      )}

      {/* Metric Builder: published dashboard metric widgets */}
      {dashboardVisible.has("metric_widgets") && dashboardMetricsList.length > 0 && (
        <div className="space-y-2" data-testid="section-metric-widgets">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Tracked Metrics
          </h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {dashboardMetricsList.map((m) => (
              <MetricWidget
                key={m.id}
                metricId={m.id}
                label={m.label}
                description={m.description}
                displayFields={m.displayFields ?? []}
                defaultSortField={m.defaultSortField}
                defaultSortDirection={m.defaultSortDirection}
                chartEnabled={m.chartEnabled}
                defaultView={m.defaultView}
                resultType={m.resultType}
                conditions={m.conditions}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gate I10: Interaction Detail Drawer - always mounted, controlled by open state */}
      <InteractionDetailDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) setSelectedInteraction(null);
        }}
        interaction={selectedInteraction}
        animalId={selectedInteraction?.animalId || ""}
        onInteractionChange={(newInteraction) => setSelectedInteraction(newInteraction as DashboardInteraction)}
      />

      {animalDrawerId && (
        <AnimalDetailDrawer
          open={animalDrawerOpen}
          onOpenChange={(open) => {
            setAnimalDrawerOpen(open);
            if (!open) setAnimalDrawerId(null);
          }}
          animalId={animalDrawerId}
          context="animals"
        />
      )}

      {linkerAnimalId && (
        <EntityLinkerDialog
          open={linkerOpen}
          onOpenChange={(open) => {
            setLinkerOpen(open);
            if (!open) {
              setLinkerAnimalId(null);
              setLinkerAnimalName(undefined);
              setLinkerAnimalSpecies(undefined);
              setLinkerAnimalPhotoUrl(undefined);
            }
          }}
          entityType="animal"
          entityId={linkerAnimalId}
          entityLabel={linkerAnimalName ? `${linkerAnimalName} (${linkerAnimalId})` : linkerAnimalId}
          animalSpecies={linkerAnimalSpecies}
          animalPhotoUrl={linkerAnimalPhotoUrl}
        />
      )}
    </div>
  );
}
